import {
  Button,
  ButtonsWrapper,
  Form,
  ICONS,
  PLATFORM_THEME_COLORS,
  TYPOGRAPHY_SIZE,
  Typography,
  ColorLabel,
} from '@thinknet-gmbh/platform-common-ui';
import {
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
  GanttAccessRightsEnum,
} from '@thinknet-gmbh/platform-common';
import { useTranslation } from 'react-i18next';

import { ICompanyGantt } from 'src/pages/settings/components/company/company.interfaces';
import { GanttFormList } from 'src/pages/settings/components/company/components/company-gantts/components/gantt-form-list';
import { useUpsertGantts } from 'src/hooks/gantts/upsert-gantts.hook';
import PermissionCheck from 'src/components/permission-check';

import { GanttContainer, InfoElement, GanttsWrapper, Header } from './gantt-info.styles';

const GanttInfo = ({
  companyId,
  member,
  data,
  onClickEdit,
}: {
  companyId: string;
  member: any;
  data: ICompanyGantt[];
  onClickEdit: () => void;
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'settings', 'gantt']);

  const upsertGantts = useUpsertGantts();

  const onFinish = (values: any) => {
    upsertGantts({
      variables: { payload: values.gantts.map((gantt: any) => ({ ...gantt, companyId })) },
    });

    form.setFieldsValue({ gantts: [] });
  };

  return (
    <GanttContainer>
      <Form layout="vertical" onFinish={onFinish} form={form}>
        <Form.List name="gantts">
          {(fields, { add, remove }) => (
            <>
              <Header>
                <Typography size={TYPOGRAPHY_SIZE.medium} weight="bold">
                  {t('common:labels.gantt')}
                </Typography>

                <ButtonsWrapper>
                  <PermissionCheck
                    permission={PLATFORM_PERMISSIONS.permissionGroup}
                    action={PERMISSION_ACTIONS.update}
                  >
                    <Button
                      icon={ICONS.plus}
                      ghost
                      type="primary"
                      onClick={() => add()}
                      iconColor={PLATFORM_THEME_COLORS.primary.blue}
                    >
                      {t('settings:company.buttons.addNewGantt')}
                    </Button>
                  </PermissionCheck>
                  {!!data.length && (
                    <Button
                      icon={ICONS.editAlt}
                      ghost
                      type="primary"
                      onClick={onClickEdit}
                      iconColor={PLATFORM_THEME_COLORS.primary.blue}
                    >
                      {t('common:buttons.edit')}
                    </Button>
                  )}
                </ButtonsWrapper>
              </Header>

              <GanttsWrapper>
                {data.map((item: any) => (
                  <div key={item._id}>
                    <InfoElement>
                      <Typography weight="bold">
                        {t('settings:company.labels.ganttName')}:
                      </Typography>
                      <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                        {item.name}
                      </Typography>
                      {!member.ganttsAccess.some(
                        ({ ganttId, accessRight }: any) =>
                          ganttId === item._id && accessRight === GanttAccessRightsEnum.EDIT
                      ) && (
                        <Typography fontColor={PLATFORM_THEME_COLORS.grayVariations.murky}>
                          ({t('gantt:labels.viewOnly')})
                        </Typography>
                      )}
                    </InfoElement>
                    <InfoElement>
                      <Typography weight="bold">
                        {t('settings:holidayManager.labels.holidayLocation')}:
                      </Typography>
                      <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                        {item.holidayLocation.name}
                      </Typography>
                    </InfoElement>
                    <InfoElement>
                      <Typography weight="bold">{t('gantt:labels.weekends')}:</Typography>
                      <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                        {item.weekends
                          .map((weekday: string) => t(`common:days.${weekday}`))
                          .join(', ')}
                      </Typography>
                    </InfoElement>
                    <InfoElement>
                      <Typography
                        fontColor={PLATFORM_THEME_COLORS.grayVariations.murky}
                        weight="bold"
                      >
                        {t('settings:company.labels.daysColor')}
                      </Typography>
                    </InfoElement>

                    <InfoElement>
                      <Typography weight="bold">
                        {t('settings:company.labels.colorSchema.holiday')}:
                      </Typography>
                      <ColorLabel
                        color={
                          item.colorSchema?.holidayColor || PLATFORM_THEME_COLORS.tone.lightYellow
                        }
                      />
                    </InfoElement>
                    <InfoElement>
                      <Typography weight="bold">
                        {t('settings:company.labels.colorSchema.today')}:
                      </Typography>
                      <ColorLabel
                        color={item.colorSchema?.todayColor || PLATFORM_THEME_COLORS.tone.lightBlue}
                      />
                    </InfoElement>
                    <InfoElement>
                      <Typography weight="bold">
                        {t('settings:company.labels.colorSchema.weekend')}:
                      </Typography>
                      <ColorLabel
                        color={
                          item.colorSchema?.weekendColor ||
                          PLATFORM_THEME_COLORS.grayVariations.light
                        }
                      />
                    </InfoElement>
                  </div>
                ))}
              </GanttsWrapper>
              <GanttFormList form={form} data={[]} fields={fields} remove={remove} />
              {fields.length > 0 && (
                <ButtonsWrapper>
                  <Button type="primary" htmlType="submit">
                    {t('settings:company.buttons.add')}
                  </Button>
                  <Button onClick={() => form.setFieldsValue({ gantts: [] })}>
                    {t('common:buttons.cancel')}
                  </Button>
                </ButtonsWrapper>
              )}
            </>
          )}
        </Form.List>
      </Form>
    </GanttContainer>
  );
};

export default GanttInfo;
